// ============================================
// APP VERSION (semantic: major.minor.patch)
// ============================================
export const APP_VERSION = "10.0.0";

// What's New modal: show once per version (first login or refresh after update)
export const RELEASE_SEEN_STORAGE_KEY = "pos_release_seen_version";
// Features to show in the What's New modal for this version (update when releasing)
export const RELEASE_FEATURES = [
  "Enhanced UI for a smoother and more intuitive user experience",
  "Improved application performance and overall system stability",
  "Strengthened security for safer and more reliable usage",
  "Upgraded PRA and FBR features for better compliance and functionality",
  "Further Tax implementation on invoices",
  "New Stock Management module",
  "Improved error handling and system logging"
];

// ============================================
// HS CODE HINTS
// ============================================
export const HS_CODE_HINTS = [
  { label: "General Medicine", code: "300490" },
  { label: "Vaccine", code: "300220" },
  { label: "Insulin", code: "300431" },
  { label: "Nutrition powder", code: "190110" },
  { label: "Nutrition liquid / drinks", code: "220299" },
  { label: "Hand sanitizer / disinfectant", code: "380894" },
  { label: "Toilet / bath soap", code: "340111" },
  { label: "Shampoo", code: "330510" },
  { label: "Hair conditioner", code: "330590" },
  { label: "Condoms", code: "401410" },
  { label: "Medical / diagnostic device", code: "901890" },
  { label: "Thermometer", code: "902519" },
  { label: "Toothbrush", code: "960321" },
];

// ============================================
// DATABASE CONSTANTS
// ============================================
// Dining Database
export const DINING_DB_NAME = "pos_dining";
export const DINING_DB_VERSION = 7;
export const DINING_STORE_TABLES = "tables";
export const DINING_STORE_ORDERS = "orders";
export const DINING_STORE_FREQUENT = "frequent_items";
export const DINING_STORE_SETTINGS = "settings";
export const DINING_STORE_KITCHEN_RECEIPTS = "kitchen_receipts";
export const DINING_STORE_HELD_ORDERS = "held_orders";
export const DINING_PER_HEAD_SETTING_KEY = "perHeadBase";
export const DINING_DEFAULT_PER_HEAD_BASE = 120;
export const DINING_KITCHEN_TOKEN_SETTING_KEY = "kitchenToken";
export const DINING_DEFAULT_KITCHEN_TOKEN = 1;

// Category Layout Database
export const CATEGORY_LAYOUT_DB_NAME = "pos-layout-db";
export const CATEGORY_LAYOUT_STORE = "categories";
export const CATEGORY_SIDEBAR_KEY = "category-sidebar";

// ============================================
// UI/LAYOUT CONSTANTS
// ============================================
export const CATEGORY_SIDEBAR_MIN_WIDTH = 220;
export const CATEGORY_SIDEBAR_MAX_WIDTH = 420;
export const CATEGORY_SIDEBAR_DEFAULT_WIDTH = 280;

export const CART_TOTALS_MIN_WIDTH = 300;
export const CART_TOTALS_MAX_WIDTH = 600;
export const CART_TOTALS_DEFAULT_WIDTH = 350;

export const INVOICE_PAGE_LIMIT = 10;

export const CHART_COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#845EC2"];

// ============================================
// BUSINESS LOGIC CONSTANTS
// ============================================
// Invoice Types
export const INVOICE_TYPE_POS = "POS";
export const INVOICE_TYPE_DIGITAL_INVOICING = "DIGITAL_INVOICING";

// Payment Modes
export const PAYMENT_MODE_CASH = "Cash";
export const PAYMENT_MODE_CREDIT = "Credit";
export const PAYMENT_MODE_DEBIT_CARD = "Card";
export const PAYMENT_MODE_CASH_CODE = 1;
export const PAYMENT_MODE_CREDIT_CODE = 3;
export const PAYMENT_MODE_DEBIT_CARD_CODE = 2;

// Sales Tax Rates
export const SALES_TAX_0_PERCENT = "0%";
export const SALES_TAX_16_PERCENT = "16%";
export const SALES_TAX_18_PERCENT = "18%";
export const SALES_TAX_20_PERCENT = "25%";
export const SALES_TAX_RATES = [
  SALES_TAX_0_PERCENT,
  SALES_TAX_16_PERCENT,
  SALES_TAX_18_PERCENT,
  SALES_TAX_20_PERCENT,
];

// Unit of Measurement (UoM)
export const UOM_NUMBERS_PIECES_UNITS = "Numbers, pieces, units";
export const UOM_METRIC_TON = "MT";
export const UOM_KILOGRAM = "KG";
export const UOM_GRAMS = "Grams";
export const UOM_MILLIGRAM = "MG";
export const UOM_PACKS = "Packs";
export const UOM_OPTIONS = [
  UOM_NUMBERS_PIECES_UNITS,
  UOM_METRIC_TON,
  UOM_KILOGRAM,
  UOM_GRAMS,
  UOM_MILLIGRAM,
  UOM_PACKS,
];

// POS Fee
export const POS_FEE = 1;

// PRA (Punjab Revenue Authority) – per-head charge as line item (2nd Schedule service code)
export const PRA_PER_HEAD_ITEM_CODE = "PERHEAD";
export const PRA_PER_HEAD_PCT_CODE = "99630000"; // Food/beverage serving services (adjust per 2nd Schedule if required)

// Default Category
export const DEFAULT_CATEGORY = "All";

// Default Product Stock
export const DEFAULT_PRODUCT_STOCK = 100000;

// Low stock threshold: show notification when product stock is below this value
export const LOW_STOCK_THRESHOLD = 100;

// ============================================
// PROVINCES
// ============================================
export const PROVINCES = [
  { label: "Punjab", value: "Punjab" },
  { label: "Sindh", value: "Sindh" },
  { label: "Khyber Pakhtunkhwa", value: "KPK" },
  { label: "Balochistan", value: "Balochistan" },
  { label: "Islamabad", value: "Islamabad" },
  { label: "Azad Jammu & Kashmir", value: "AJK" },
  { label: "Gilgit Baltistan", value: "GB" },
];

// ============================================
// PHARMACY TAX KEYWORDS
// ============================================
export const PHARMACY_TAX_EXEMPT_KEYWORDS = [
  "tablet",
  "tab",
  "tabs",
  "capsule",
  "cap",
  "caps",
  "syrup",
  "injection",
  "inj",
  "iv",
  "cream",
  "ointment",
  "gel",
  "lotion",
  "drops",
  "suspension",
];

export const PHARMACY_TAX_18_PERCENT_KEYWORDS = [
  "cosmetic",
  "supplement",
  "nutraceutical",
  "protein",
  "beauty",
  "shampoo",
  "soap",
  "face wash",
  "makeup",
  "pamper",
  "pampers",
  "condom",
  "condoms",
];

// ============================================
// DIGITAL INVOICING SCENARIOS
// ============================================
export const scenariosForDigitalInvoicing = [
  {
    heading: "SN001: Sale of Standard Rate Goods to Registered Buyers.",
    discription: "This applies to the sale of goods subject to the standard sales tax rate made to sales tax registered buyers. These buyers can usually claim input tax credits, allowing them to offset the tax paid on purchases against the tax due on their own sales."
  },
  {
    heading: "SN002: Sale of Standard Rate Goods to Unregistered Buyers.",
    discription: "When goods subject to the standard sales tax rate are sold to buyers who are not registered for sales tax (usually individual consumers or small businesses not registered for tax) the seller charges the full sales tax. Since the buyer is unregistered, they cannot claim input tax credits. This is often a business-to- consumer (B2C) sale."
  },
  {
    heading: "SN003: Sale of Steel (Melted and Re-Rolled) (Billets, Ingots and Long Bars).",
    discription: "The steel sector is governed by strict traceability and sector-specific rules. These goods are commonly traded by re-rollers and manufacturers. Tax authorities often regulate their tax treatment distinctly, sometimes applying specific tax rates or requiring compliance with additional notifications or schedules (like an SRO)."
  },
  {
    heading: "SN004: Sale of Steel Scrap by Ship Breakers:",
    discription: "Ship breakers dismantle old ships and recover scrap steel, which they then sell. This scrap steel is often treated separately for tax purposes, recognizing the different industry context. Special rates or exemptions might apply, and tax compliance is tailored to the ship-breaking sector."
  },
  {
    heading: "SN005: Sales of Reduced Rate Goods (Eighth Schedule):",
    discription: "Certain goods are taxed at a reduced sales tax rate (lower than the standard rate) to encourage affordability or protect consumers. The Eighth Schedule lists these goods commonly basic food items, medicines, or essential commodities."
  },
  {
    heading: "SN006: Sale of Exempt Goods (Sixth Schedule):",
    discription: "Goods listed in the Sixth Schedule are exempt from sales tax, meaning sellers do not charge sales tax on these goods. This exemption is usually to reduce the tax burden on essential or socially important items, such as certain agricultural products, medicines, or basic necessities."
  },
  {
    heading: "SN007: Sale Of Zero-Rated Goods (Fifth Schedule):",
    discription: "Zero-rated goods are those on which sales tax is charged at 0%. While the seller does not charge sales tax to the buyer, the seller can claim input tax credits on purchases related to these goods. This is often applied to exported goods or specific industries to promote exports and reduce tax layering."
  },
  {
    heading: "SN008: Sale of 3rd Schedule Goods:",
    discription: "Items listed under the Third Schedule are subject to sales tax on the basis of their printed retail price rather than the transaction value. In such cases, manufacturers or importers are responsible for paying sales tax at the point of production or import, not at the point of sale."
  },
  {
    heading: "SN009: Purchase From Registered Cotton Ginners.",
    discription: "Purchases from registered cotton ginners, subject to specific rules under cotton trade taxation. May involve reverse charge or input tax mechanisms."
  },
  {
    heading: "SN010: Sale Of Telecom Services by Mobile Operators.",
    discription: "Mobile operators provide telecom services (calls, data, SMS). These services are typically taxed under specific rules separate from goods, sometimes including additional regulatory fees. Invoices must account for both FED and GST, possibly varying by province."
  },
  {
    heading: "SN011: Sale of Steel through Toll Manufacturing (Billets, Ingots and Long Bars).",
    discription: "Toll manufacturing involves a third-party processing raw steel into finished billets, ingots, or long bars on behalf of another business. The processor issues an invoice for conversion charges (service), while the principal may generate a separate invoice for the actual product sale. The sales tax treatment can differ because ownership and processing roles are split."
  },
  {
    heading: "SN012: Sale Of Petroleum Products.",
    discription: "Petroleum products like petrol, diesel, or lubricants often have distinct sales tax rates or are subject to federal excise duties due to their economic and environmental importance. These products may also have multiple layers of tax or additional regulatory levies."
  },
  {
    heading: "SN013: Sale Of Electricity to Retailers.",
    discription: "Selling electricity to retailers who distribute to end consumers can have unique tax implications. Sales tax or federal excise duty might be applied differently depending on whether the electricity is sold wholesale or retail, with some exemptions for consumers."
  },
  {
    heading: "SN014: Sale of Gas to CNG Stations.",
    discription: "Natural gas sold to CNG (Compressed Natural Gas) filling stations may have special tax treatment to promote cleaner fuels or regulate the energy sector. The tax rate and compliance requirements can differ from other gas sales."
  },
  {
    heading: "SN001: Sale of Standard Rate Goods to Registered Buyers.",
    discription: "This applies to the sale of goods subject to the standard sales tax rate made to sales tax registered buyers. These buyers can usually claim input tax credits, allowing them to offset the tax paid on purchases against the tax due on their own sales."
  },
  {
    heading: "SN015: Sale of Mobile Phones.",
    discription: "Sales of mobile handsets often attract standard sales tax but might be subject to additional duties or regulatory charges, especially given the value of these goods. Governments sometimes adjust tax rates on mobile phones to encourage or discourage imports."
  },
  {
    heading: "SN016: Processing / Conversion of Goods.",
    discription: "This refers to services where raw materials or semi-finished goods are converted into finished products through manufacturing or processing. It involves charging for the value-added process (like dyeing, packaging, machining), not the goods themselves. Sales tax on such services can differ from that on goods sales."
  },
  {
    heading: "SN017: Sale of Goods Where FED Is Charged in ST Mode.",
    discription: "Federal Excise Duty (FED) may be charged alongside sales tax, but in some cases, FED is collected through the sales tax system (\"ST mode\"). This means the seller includes FED in the sales tax invoice, streamlining collection."
  },
  {
    heading: "SN018: Sale Of Services Where FED Is Charged in ST Mode.",
    discription: "Certain services (e.g., advertisement, franchise, insurance) are liable to FED but invoiced under the sales tax framework. This allows FBR to monitor service sector revenue streams without requiring separate returns for FED and sales tax."
  },
  {
    heading: "SN019: Sale of Services (as per ICT Ordinance).",
    discription: "Businesses providing services such as consultancy, software development, and IT solutions in ICT are taxed under a distinct ordinance. This can include variations in rates or exemptions."
  },
  {
    heading: "SN020: Sale of Electric Vehicles.",
    discription: "Electric vehicles may be incentivized through reduced sales tax rates or exemptions to encourage environmentally friendly transportation. Invoices must categorize these vehicles distinctly (not as hybrids or combustion), and declare engine/battery specs to validate tax benefits."
  },
  {
    heading: "SN021: Sale of Cement /Concrete Block.",
    discription: "Cement and concrete blocks are taxed at the standard rate and are subject to strict regulation due to their environmental impact and role in construction. These products are also subject to input-output ratios tracking due construction regulations."
  },
  {
    heading: "SN022: Sale of Potassium Chlorate.",
    discription: "Potassium chlorate is a sensitive chemical used primarily in matchstick manufacturing and regulated under special rules. It is subject to fixed tax per kilogram (weight) rather than value."
  },
  {
    heading: "SN023: Sale of CNG.",
    discription: "Sales of Compressed Natural Gas involve regulated pricing structures and specific tax treatments that may include both FED and sales tax. CNG stations must issue invoices for every sale, indicating the volume sold, tax applied, and billing rates. These transactions are generally high-frequency and often managed via automated dispensing and billing systems."
  },
  {
    heading: "SN024: Sale Of Goods Listed in SRO 297(1)/2023.",
    discription: "This SRO notifies specific goods subject to reduced, conditional, or fixed-rate taxation. These may include solar equipment, medical devices, or energy-efficient appliances may include solar equipment, medical devices, or energy-efficient appliances. Businesses dealing in such goods must identify the correct serial numbers and apply appropriate tax treatments in their invoices."
  },
  {
    heading: "SN025: Drugs Sold at Fixed ST Rate Under Serial 81 Of Eighth Schedule Table 1.",
    discription: "Certain pharmaceutical products are taxed at a fixed sales tax rate under serial 81 of the Eighth Schedule. These rates are often lower to make medicines affordable, and the fixed rate means the tax is a set amount or percentage regardless of price variations."
  },
  {
    heading: "SN026: Sale Of Goods at Standard Rate to End Consumers by Retailers.",
    discription: "Retailers selling taxable goods directly to end consumers must apply the standard rate of sales tax at the point of sale. These transactions are typically conducted through Point-of-Sale (POS) systems integrated with FBR's IRIS platform. Since consumers are typically unregistered, retailers collect the tax without input tax credit implications."
  },
  {
    heading: "SN027: Sale Of 3rd Schedule Goods to End Consumers by Retailers.",
    discription: "Retailers selling goods under the 3rd Schedule, such as branded FMCGs, must charge and report sales tax based on the maximum retail price (MRP), not the transactional or discounted."
  },
  {
    heading: "SN028: Sale of Goods at Reduced Rate to End Consumers by Retailers.",
    discription: "Certain essential goods, such as baby milk and books, are subject to a reduced tax rate lower than the standard 18% when sold to end consumers. This preferential rate is intended to keep vital or socially important products affordable. Retailers selling such goods must ensure that the products are correctly classified under the applicable tax schedule to qualify for the reduced rate."
  },
];
